import { supabase } from '@/utils/supabase';

/** Delivery state of one website-lead email (profile_lead_notification_deliveries.status). */
export type ProfileLeadEmailState =
  | 'pending'
  | 'processing'
  | 'sent'
  | 'failed'
  /** An attempt may have reached Resend, but delivery was never confirmed. */
  | 'unconfirmed'
  | 'skipped_disabled'
  | 'skipped_expired';

export type ProfileLeadEmailType = 'profile_lead_lo_notification' | 'profile_lead_consumer_confirm';

export interface ProfileLeadEmailStatus {
  status: ProfileLeadEmailState;
  sentAt: string | null;
  attemptCount: number;
  generation: number;
  /** Delivery error; returned only to users who can see every Artemis record. */
  error: string | null;
  /** Send again is allowed now (view-all users only); the server decides. */
  resendable: boolean;
}

/** A message sent from a public loan officer page, as Artemis Online Leads shows it. */
export interface ProfileLeadQueueLead {
  /** The submission id; also the email deep link's `lead` parameter. */
  id: string;
  borrowerName: string;
  email: string | null;
  phone: string | null;
  message: string | null;
  loanAmount: number | null;
  propertyType: string | null;
  assignedUserId: string | null;
  ownerName: string | null;
  pageOfficerName: string | null;
  pageSlug: string | null;
  createdAt: string;
  /** Empty for a message saved before email tracking began. */
  emails: {
    officer?: ProfileLeadEmailStatus;
    customer?: ProfileLeadEmailStatus;
  };
}

export interface ProfileLeadQueueResult {
  status: 'ok' | 'unavailable' | 'forbidden';
  canResend: boolean;
  records: ProfileLeadQueueLead[];
}

export async function fetchProfileLeadQueue(): Promise<ProfileLeadQueueResult> {
  const { data, error } = await supabase.rpc('profile_lead_queue');
  if (error) {
    throw new Error('Unable to load website leads.');
  }
  if (
    !data
    || !['ok', 'unavailable', 'forbidden'].includes(data.status)
    || !Array.isArray(data.records)
  ) {
    throw new Error('Invalid website lead response.');
  }
  return data as ProfileLeadQueueResult;
}

/** Queues a failed or skipped email to send again on the next 10-minute run. */
export async function resendProfileLeadEmail(
  submissionId: string,
  notificationType: ProfileLeadEmailType
): Promise<void> {
  const { data, error } = await supabase.rpc('profile_lead_notification_resend', {
    p_submission_id: submissionId,
    p_notification_type: notificationType
  });
  if (error || data?.status !== 'queued') {
    throw new Error('That email could not be queued again. Refresh to see its current status.');
  }
}
